if (!customElements.get('bn-product-media-controls')) {
  class BnProductMediaControls extends HTMLElement {
    get thumbnails() {
      return [...this.querySelectorAll('.thumbnail-list button.thumbnail')].filter((button) => !button.closest('[hidden]'));
    }

    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.addEventListener('click', this.onClick.bind(this));
      this.viewer = this.querySelector('.product__media-list');
      this.currentIndex = 0;
      this.bindTouchSwipe();
      const thumbnailList = this.querySelector('.thumbnail-list');
      if (thumbnailList) {
        this.observer = new MutationObserver(() => this.update());
        this.observer.observe(thumbnailList, { attributes: true, subtree: true, attributeFilter: ['aria-current'] });
      }
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    onClick(event) {
      const zoom = event.target.closest('[data-bn-media-zoom]');
      if (zoom) {
        event.preventDefault();
        const activeMedia = this.querySelector('.product__media-item.is-active') || this.querySelector('.product__media-item');
        const openerButton = activeMedia?.querySelector('.product__media-toggle');
        const modalSelector = openerButton?.closest('modal-opener')?.dataset.modal;
        const modal = modalSelector ? document.querySelector(modalSelector) : null;

        if (modal && typeof modal.show === 'function') {
          modal.show(openerButton);
        } else {
          openerButton?.click();
        }
        return;
      }

      const control = event.target.closest('[data-bn-media-direction]');
      if (!control) return;
      const thumbnails = this.thumbnails;
      if (thumbnails.length < 2) return;
      const current = Math.max(0, thumbnails.findIndex((button) => button.getAttribute('aria-current') === 'true'));
      const offset = control.dataset.bnMediaDirection === 'next' ? 1 : -1;
      thumbnails[(current + offset + thumbnails.length) % thumbnails.length].click();
    }

    bindTouchSwipe() {
      if (!this.viewer || this.viewer.dataset.bnDragReady === 'true') return;
      this.viewer.dataset.bnDragReady = 'true';
      let startX = 0;
      let moved = false;

      this.viewer.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        startX = event.clientX;
        moved = false;
        this.viewer.classList.add('is-dragging');
        this.viewer.setPointerCapture?.(event.pointerId);
      });

      this.viewer.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'mouse') return;
        if (!this.viewer.classList.contains('is-dragging')) return;
        const distance = event.clientX - startX;
        if (Math.abs(distance) > 4) moved = true;
        this.viewer.style.setProperty('--bn-media-drag', `${distance}px`);
      });

      const finishDrag = (event) => {
        if (event.pointerType === 'mouse') return;
        if (!this.viewer.classList.contains('is-dragging')) return;
        const distance = event.clientX - startX;
        this.viewer.classList.remove('is-dragging');
        this.viewer.releasePointerCapture?.(event.pointerId);
        this.viewer.style.removeProperty('--bn-media-drag');
        if (!moved) return;
        const direction = distance < 0 ? 'next' : 'previous';
        const button = this.querySelector(`[data-bn-media-direction="${direction}"]`);
        button?.click();
      };

      this.viewer.addEventListener('pointerup', finishDrag);
      this.viewer.addEventListener('pointercancel', finishDrag);
      this.viewer.addEventListener('click', (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }, true);
    }

    update() {
      const thumbnails = this.thumbnails;
      const hidden = thumbnails.length < 2;
      this.querySelectorAll('[data-bn-media-direction]').forEach((button) => { button.hidden = hidden; });
      const selectedIndex = thumbnails.findIndex((button) => button.getAttribute('aria-current') === 'true');
      this.currentIndex = Math.max(0, selectedIndex);
      this.viewer?.style.setProperty('--bn-media-index', this.currentIndex);
      const zoom = this.querySelector('[data-bn-media-zoom]');
      const activeMedia = this.querySelector('.product__media-item.is-active') || this.querySelector('.product__media-item');
      const mediaContainer = activeMedia?.querySelector('.product-media-container');
      if (zoom && mediaContainer && zoom.parentElement !== mediaContainer) mediaContainer.append(zoom);
    }
  }

  customElements.define('bn-product-media-controls', BnProductMediaControls);
}

if (!customElements.get('bn-product-highlights-tabs')) {
  class BnProductHighlightsTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.tabs = [...this.querySelectorAll('[data-bn-highlight-tab]')];
      this.panels = [...this.querySelectorAll('[data-bn-highlight-panel]')];
      this.panelsContainer = this.querySelector('[data-bn-highlight-panels]');
      this.specTrack = this.querySelector('[data-bn-spec-track]');
      this.specControls = [...this.querySelectorAll('[data-bn-spec-direction]')];
      this.boxTrack = this.querySelector('[data-bn-box-track]');
      this.boxControls = [...this.querySelectorAll('[data-bn-box-direction]')];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab));
        tab.addEventListener('keydown', (event) => this.onKeydown(event));
      });

      this.boxControls.forEach((button) => {
        button.addEventListener('click', () => this.scrollBox(button.dataset.bnBoxDirection));
      });

      this.specControls.forEach((button) => {
        button.addEventListener('click', () => this.scrollSpecs(button.dataset.bnSpecDirection));
      });

      this.boxTrack?.addEventListener('scroll', () => this.updateBoxControls(), { passive: true });
      this.specTrack?.addEventListener('scroll', () => this.updateSpecControls(), { passive: true });
      this.resizeObserver = new ResizeObserver(() => {
        this.updateBoxControls();
        this.updateSpecControls();
      });
      if (this.boxTrack) this.resizeObserver.observe(this.boxTrack);
      if (this.specTrack) this.resizeObserver.observe(this.specTrack);

      this.handleWindowResize = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => {
          this.updatePanelHeight();
          this.updateBoxControls();
          this.updateSpecControls();
        });
      };
      window.addEventListener('resize', this.handleWindowResize, { passive: true });
      document.fonts?.ready.then(() => this.updatePanelHeight());
      requestAnimationFrame(() => {
        this.updatePanelHeight();
        this.updateBoxControls();
        this.updateSpecControls();
      });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.handleWindowResize);
      cancelAnimationFrame(this.resizeFrame);
    }

    activate(activeTab, moveFocus = false) {
      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== activeTab.getAttribute('aria-controls');
      });

      requestAnimationFrame(() => {
        this.updatePanelHeight();
        this.updateBoxControls();
        this.updateSpecControls();
      });
      if (moveFocus) activeTab.focus();
    }

    updatePanelHeight() {
      if (!this.panelsContainer || !this.panels.length || this.panelsContainer.clientWidth === 0) return;
      let maxHeight = 0;
      this.panelsContainer.style.minHeight = '';

      this.panels.forEach((panel) => {
        const wasHidden = panel.hidden;
        panel.hidden = false;
        panel.classList.add('is-measuring');
        maxHeight = Math.max(maxHeight, panel.scrollHeight);
        panel.classList.remove('is-measuring');
        panel.hidden = wasHidden;
      });

      if (maxHeight > 0) this.panelsContainer.style.minHeight = `${Math.ceil(maxHeight)}px`;
    }

    scrollBox(direction) {
      if (!this.boxTrack) return;
      const firstItem = this.boxTrack.querySelector('.bn-v3-product-main__box-item');
      const gap = Number.parseFloat(getComputedStyle(this.boxTrack).columnGap) || 0;
      const distance = (firstItem?.getBoundingClientRect().width || this.boxTrack.clientWidth * 0.8) + gap;

      this.boxTrack.scrollBy({
        left: direction === 'next' ? distance : -distance,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    scrollSpecs(direction) {
      if (!this.specTrack) return;
      const firstItem = this.specTrack.querySelector('.bn-highlight');
      const gap = Number.parseFloat(getComputedStyle(this.specTrack).columnGap) || 0;
      const distance = (firstItem?.getBoundingClientRect().width || this.specTrack.clientWidth * 0.8) + gap;

      this.specTrack.scrollBy({
        left: direction === 'next' ? distance : -distance,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    updateBoxControls() {
      if (!this.boxTrack || !this.boxControls.length) return;
      const maxScroll = Math.max(0, this.boxTrack.scrollWidth - this.boxTrack.clientWidth);
      const hasOverflow = maxScroll > 2;

      this.boxControls.forEach((button) => {
        button.hidden = !hasOverflow;
        button.disabled = button.dataset.bnBoxDirection === 'previous'
          ? this.boxTrack.scrollLeft <= 2
          : this.boxTrack.scrollLeft >= maxScroll - 2;
      });
    }

    updateSpecControls() {
      if (!this.specTrack || !this.specControls.length) return;
      const maxScroll = Math.max(0, this.specTrack.scrollWidth - this.specTrack.clientWidth);
      const hasOverflow = maxScroll > 2;

      this.specControls.forEach((button) => {
        button.hidden = !hasOverflow;
        button.disabled = button.dataset.bnSpecDirection === 'previous'
          ? this.specTrack.scrollLeft <= 2
          : this.specTrack.scrollLeft >= maxScroll - 2;
      });
    }

    onKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      let nextIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      if (nextIndex === undefined) return;

      event.preventDefault();
      this.activate(this.tabs[nextIndex], true);
    }
  }

  customElements.define('bn-product-highlights-tabs', BnProductHighlightsTabs);
}

if (!customElements.get('bn-buy-button-price')) {
  class BnBuyButtonPrice extends HTMLElement {
    connectedCallback() {
      if (this.unsubscribe || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (String(event.data.sectionId) !== String(this.dataset.sectionId)) return;

        const sourceButton = event.data.html.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
        const sourcePrices = sourceButton?.querySelector('bn-buy-button-price');

        if (sourcePrices) {
          this.innerHTML = sourcePrices.innerHTML;
        } else {
          this.replaceChildren();
        }
      });
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
    }
  }

  customElements.define('bn-buy-button-price', BnBuyButtonPrice);
}

if (!customElements.get('bn-product-accordion')) {
  class BnProductAccordion extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.panel = this.querySelector('[data-bn-accordion-panel]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.desktopInteraction = this.dataset.desktopInteraction || 'hover';
      this.closeTimer = undefined;

      if (!this.details || !this.summary || !this.panel) return;
      this.expanded = this.details.open;
      this.summary.addEventListener('click', this.onSummaryClick);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
    }

    disconnectedCallback() {
      this.summary?.removeEventListener('click', this.onSummaryClick);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      window.clearTimeout(this.closeTimer);
      this.animation?.cancel();
    }

    onSummaryClick = (event) => {
      event.preventDefault();
      this.setExpanded(!this.expanded);
    };

    onMouseEnter = () => {
      if (!this.desktopHover?.matches || this.desktopInteraction !== 'hover') return;
      window.clearTimeout(this.closeTimer);
      this.setExpanded(true);
    };

    onMouseLeave = () => {
      if (!this.desktopHover?.matches || this.desktopInteraction !== 'hover') return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.setExpanded(false), 140);
    };

    setExpanded(expanded) {
      if (expanded === this.expanded && !this.animation) return;
      this.expanded = expanded;
      this.animation?.cancel();

      if (this.reducedMotion.matches) {
        this.details.open = expanded;
        this.panel.style.removeProperty('height');
        this.panel.style.removeProperty('opacity');
        return;
      }

      if (expanded) this.details.open = true;

      const startHeight = expanded ? 0 : this.panel.getBoundingClientRect().height;
      const endHeight = expanded ? this.panel.scrollHeight : 0;

      this.animation = this.panel.animate(
        [
          { height: `${startHeight}px`, opacity: expanded ? 0 : 1 },
          { height: `${endHeight}px`, opacity: expanded ? 1 : 0 },
        ],
        { duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );

      this.animation.onfinish = () => {
        this.details.open = expanded;
        this.panel.style.removeProperty('height');
        this.panel.style.removeProperty('opacity');
        this.animation = undefined;
      };
    }
  }

  customElements.define('bn-product-accordion', BnProductAccordion);
}
